import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskedLines, EASE } from "./motion";
import { scrollToId } from "../lib/scroll";
import { useLang } from "../i18n";

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
  const mx = useSpring(useMotionValue(0), { stiffness: 40, damping: 20 });
  const my = useSpring(useMotionValue(0), { stiffness: 40, damping: 20 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width - 0.5) * -24);
    my.set(((e.clientY - r.top) / r.height - 0.5) * -16);
  };

  return (
    <section id="top" ref={ref} onMouseMove={onMove} data-testid="hero-section" className="relative h-[100svh] min-h-[640px] overflow-hidden bg-[#1C1D1A]">
      <motion.div
        initial={{ clipPath: "inset(14% 10% 14% 10% round 220px 220px 0 0)", scale: 1.25 }}
        animate={{ clipPath: "inset(0% 0% 0% 0% round 0px 0px 0 0)", scale: 1 }}
        transition={{ duration: 1.8, ease: EASE }}
        className="absolute inset-0"
      >
        <motion.div style={{ y: imgY }} className="absolute -inset-8">
          <motion.img style={{ x: mx, y: my, scale: 1.04 }} src="/img/monsaraz.jpg" alt={t.hero.alt} className="w-full h-full object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#1C1D1A]/55 via-[#1C1D1A]/20 to-[#1C1D1A]/75" />
        <div className="grain" />
      </motion.div>

      <motion.div style={{ y: textY }} className="relative z-10 h-full max-w-[1440px] mx-auto px-5 md:px-10 flex flex-col justify-end pb-10 md:pb-14">
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 1 }}
          className="eyebrow text-[#EFE6DA] mb-6 flex items-center gap-3"
        >
          <span className="w-10 h-px bg-[#EFE6DA]" /> Reguengos de Monsaraz<span className="hidden sm:inline"> · Alentejo</span>
        </motion.p>
        <MaskedLines
          as="h1"
          onLoad
          baseDelay={4}
          testid="hero-title"
          lines={[t.hero.l1, <>{t.hero.l2a}<em className="italic text-[#E8B49A]">{t.hero.l2b}</em>.</>]}
          className="font-display font-light text-[#F7F5F0] text-[15vw] sm:text-7xl lg:text-[9.5rem] leading-[0.9] tracking-[-0.03em]"
        />
        <div className="mt-8 md:mt-10">
          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 1, ease: EASE }}
            className="text-[#EFE6DA] max-w-md text-sm md:text-lg leading-relaxed"
          >
            {t.hero.sub}
          </motion.p>
        </div>
      </motion.div>

      <button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToId("about")}
        className="hidden md:flex absolute z-10 right-10 top-1/2 -translate-y-1/2 flex-col items-center gap-3 text-[#EFE6DA]"
      >
        <span className="eyebrow text-[10px] [writing-mode:vertical-rl]">{t.hero.discover}</span>
        <ArrowDown className="w-4 h-4 animate-bounce" />
      </button>
    </section>
  );
};
